/**
 * The three recency flags a clinician ticks on a medication, as compact codes.
 *
 * The codes are `LS` / `N` / `C`, deliberately the same three the printed
 * medication record already uses (see `_build_legend` in
 * `backend/app/services/pdf/medication_pdf_generator.py`). One fact should not
 * have two abbreviations in front of the same reader.
 *
 * `LS` is labelled with the edit dialog's checkbox label. `N` and `C` are
 * labelled with the dialog's tooltip wording, since their checkboxes read only
 * "New" and "Change", which say nothing about the 30- and 60-day windows. The
 * dialog imports both strings from here, so the two surfaces cannot drift. This
 * is the printed legend's wording minus its "(clinic setting)" aside, which
 * says where those windows are configured rather than what the flag means.
 */

import type { PatientMedicationResponse } from '../../services/api';

export interface MedicationStatusFlag {
  /** The short code shown in the table. */
  code: string;
  /** The full phrase, for the table legend and for assistive technology. */
  label: string;
}

/** Just the flags, so callers can pass any medication-shaped record. */
export type MedicationStatusFlagSource = Pick<
  PatientMedicationResponse,
  'is_longstanding' | 'is_change' | 'is_new'
>;

export const LONGSTANDING_FLAG: MedicationStatusFlag = {
  code: 'LS',
  label: 'Longstanding',
};

export const NEW_FLAG: MedicationStatusFlag = {
  code: 'N',
  label: 'Medication added within last 30 days',
};

export const CHANGED_FLAG: MedicationStatusFlag = {
  code: 'C',
  label: 'Medication changed within last 60 days',
};

/**
 * Every recency flag the medication carries, in the printed legend's order.
 *
 * Returns all of them rather than the first, unlike the PDF's exclusive
 * indicator: a medication may be both new and changed, and the edit dialog
 * lets a clinician tick both. It also returns longstanding alongside new or
 * changed if a row somehow carries that combination. The dialog forbids it,
 * but nothing in the table does, and a cell that quietly hid one of two
 * recorded facts would be worse than one that shows an odd-looking pair.
 *
 * The flags are read for truthiness because the API marks them optional (they
 * have a server-side default, so the generated client types them `boolean?`).
 * The cost of that is worth naming: a misspelled field name reads as
 * `undefined` here rather than failing to compile.
 */
export const getMedicationStatusFlags = (
  medication: MedicationStatusFlagSource
): MedicationStatusFlag[] => {
  const flags: MedicationStatusFlag[] = [];
  if (medication.is_longstanding) {
    flags.push(LONGSTANDING_FLAG);
  }
  if (medication.is_new) {
    flags.push(NEW_FLAG);
  }
  if (medication.is_change) {
    flags.push(CHANGED_FLAG);
  }
  return flags;
};

/**
 * Every code the table can show, in the printed legend's order.
 *
 * The table prints this as a legend whenever any row carries a flag. A code is
 * only decodable if something on screen decodes it, and a hover tooltip is not
 * that something: Radix suppresses tooltips for touch pointers, so on a phone
 * the chips would be three bare letters. The legend needs no pointer at all.
 */
export const MEDICATION_STATUS_FLAG_LEGEND: readonly MedicationStatusFlag[] = [
  LONGSTANDING_FLAG,
  NEW_FLAG,
  CHANGED_FLAG,
];
